import {
	Content,
	DetailMenuItem,
	DocumentReference,
	ViewAccess,
} from '../types';
import { hasInheritedViewAccess } from '../types/view-access.interface';

export type ExternalLinkMenuHandlers = {
	onGenerate: () => void;
	onCopy: () => void;
	onRemove: () => void;
};

export function getExternalLinkMenuItems(
	hasExternalLink: boolean,
	handlers: ExternalLinkMenuHandlers,
): DetailMenuItem[] {
	if (!hasExternalLink) {
		return [
			{
				text: 'content.action.generate-external-link',
				iconStart: '@tui.link',
				handler: handlers.onGenerate,
				access: 'share',
			},
		];
	}

	return [
		{
			text: 'content.action.copy-external-link',
			iconStart: '@tui.link',
			handler: handlers.onCopy,
			access: 'share',
		},
		{
			text: 'content.action.remove-external-link',
			iconStart: '@tui.unlink',
			handler: handlers.onRemove,
			access: 'edit',
		},
	];
}

export function assembleDetailMenuItems(
	access: ViewAccess,
	...sections: DetailMenuItem[][]
): DetailMenuItem[] {
	const items = sections.flatMap((section) => [...section]);
	return items.filter(
		(i) => !i.access || hasInheritedViewAccess(access, i.access),
	);
}

export function createPath(
	content: Content,
	parentPath: DocumentReference<Content>[] | null,
): DocumentReference<Content>[] | null {
	if (!content.parent) return null;

	const path = parentPath ?? [];
	const last = path.slice(-1)[0]; // the parent would be the last one

	const includesParent = last?.id === content.parent.id;
	if (!includesParent) path.push(content.parent);

	return path;
}

/**
 * True when the portal is served from a browser on localhost (ng serve).
 * Capacitor iOS/Android WebViews also report hostname "localhost", so those
 * are excluded via Capacitor.isNativePlatform().
 */
export function isBrowserLocalhost(
	hostname = getLocationHostname(),
	isNativePlatform = getCapacitorIsNativePlatform(),
): boolean {
	if (!hostname) return false;
	if (hostname !== 'localhost' && hostname !== '127.0.0.1') return false;
	if (isNativePlatform) return false;
	return true;
}

function getLocationHostname(): string | undefined {
	const win =
		typeof globalThis !== 'undefined' && 'location' in globalThis
			? (globalThis as unknown as Window)
			: null;
	return win?.location?.hostname;
}

function getCapacitorIsNativePlatform(): boolean {
	const capacitor = (
		globalThis as unknown as {
			Capacitor?: { isNativePlatform?: () => boolean };
		}
	).Capacitor;
	return capacitor?.isNativePlatform?.() ?? false;
}

/**
 * Base URL for the external viewer: localhost when running the portal locally in a
 * browser, otherwise `externalAppUrl` from env (including Capacitor native apps).
 */
export function getExternalViewerBaseUrl(
	environment: {
		externalAppUrl?: string;
	},
	options?: { hostname?: string; isNativePlatform?: boolean },
): string {
	const onBrowserLocalhost = isBrowserLocalhost(
		options?.hostname ?? getLocationHostname(),
		options?.isNativePlatform ?? getCapacitorIsNativePlatform(),
	);
	if (onBrowserLocalhost) {
		return 'http://localhost:4203';
	}
	const url = environment.externalAppUrl;
	if (!url) {
		throw new Error(
			'externalAppUrl is required when not running on localhost',
		);
	}
	return url.replace(/\/$/, '');
}

export function buildExternalViewerUrl(
	baseUrl: string,
	externalId: string,
): string {
	const base = baseUrl.replace(/\/$/, '');
	return `${base}/view/${externalId}`;
}

export function getContentShareUrl(content: Content) {
	return `home/${content.type}/${content.id}`;
}

export function getContentLogUrl(content: Content, logId: string) {
	return `home/${content.type}/${content.id}?log=${logId}`;
}
